import { Github } from 'lucide-react';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { normalizeRepoSlug } from '../../lib/repo-slug';

export interface AgentRepoChipProps {
  /** Repo slug or GitHub URL; rendered as the bare `owner/repo`. */
  repoSlug: string;
  /** Optional destination that turns the chip into an external repository link. */
  href?: string;
  /** Render the repo with the same outlined card treatment as other agent references. */
  variant?: 'meta' | 'badge';
  /**
   * When true (default), the slug ellipsizes in a tight row. Set false for
   * breadcrumbs that must keep the full `owner/repo` visible; the title
   * beside them yields instead.
   */
  truncate?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * The GitHub icon plus an agent's `owner/repo`, the canonical way to badge the
 * repository a run targets. Used both on agent rows (via {@link AgentMetaLine})
 * and in the selected agent's header bar so the two surfaces read identically.
 * Carries `min-w-0` + `overflow-hidden` so it gives way gracefully in a tight
 * row: the text truncates, then the whole chip clips to nothing rather than
 * shoving neighbouring chips (e.g. the PR badge) off the edge.
 */
export function AgentRepoChip({
  repoSlug,
  href,
  variant = 'meta',
  truncate = true,
  className,
  'data-testid': testId,
}: AgentRepoChipProps) {
  const label = normalizeRepoSlug(repoSlug);
  const content = (
    <>
      <Github className="icon-xs shrink-0" aria-hidden="true" />
      <span className={truncate ? 'truncate' : 'whitespace-nowrap'}>{label}</span>
    </>
  );

  if (variant === 'badge') {
    return (
      <Badge
        variant="outline"
        asChild={Boolean(href)}
        className={cn(
          'min-w-0 rounded-md border-border/60 bg-muted/30 px-1.5 py-0 text-[10px] text-muted-foreground hover:border-border hover:bg-muted/60 hover:text-foreground',
          className,
        )}
        data-testid={testId}
      >
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open repository ${label} on GitHub`}
          >
            {content}
          </a>
        ) : (
          content
        )}
      </Badge>
    );
  }

  const metaClassName = cn(
    'flex items-center gap-1',
    truncate ? 'min-w-0 overflow-hidden' : 'shrink-0',
    href &&
      'rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    className,
  );

  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open repository ${label} on GitHub`}
      className={metaClassName}
      data-testid={testId}
    >
      {content}
    </a>
  ) : (
    <span className={metaClassName} data-testid={testId}>
      {content}
    </span>
  );
}
